/**
 * Testing Note: We are not testing this service directly,
 * we instead are testing components that use this service.
 * We want to test the outcome not the implementation.
 */
import { Injectable, Renderer2 } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ThemeStorageService } from './theme-storage.service';

export type ArmorTheme = 'theme-dark' | 'theme-light';
export const ArmorThemes: { dark: ArmorTheme; light: ArmorTheme } = {
  dark: 'theme-dark',
  light: 'theme-light',
};

export const storeKeys = {
  theme: 'theme',
};

@Injectable({
  providedIn: 'root',
})
export class UiThemeService {
  constructor(private store: ThemeStorageService) {}

  theme$ = new BehaviorSubject<ArmorTheme>(null);
  darkThemeEnabled: boolean;
  renderer: Renderer2;
  document: Document;

  init(renderer: Renderer2, document: Document) {
    this.renderer = renderer;
    this.document = document;
    this.setTheme(this.getPreference());
  }

  getPreference(): ArmorTheme {
    // Check for a previous dark-mode preference in local storage
    const themePref = this.store.get(storeKeys.theme);

    // If nothing in local storage check their system preference
    if (
      themePref === ArmorThemes.dark ||
      (themePref === null &&
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches)
    ) {
      return ArmorThemes.dark;
    }

    return ArmorThemes.light;
  }

  updatePreference(theme: ArmorTheme) {
    this.theme$.next(theme);
    this.store.set(storeKeys.theme, theme);
  }

  setTheme(theme: ArmorTheme) {
    Object.values(ArmorThemes).forEach((theme) =>
      this.renderer.removeClass(document.body, theme)
    );
    this.updatePreference(theme);
    this.renderer.addClass(document.body, theme);
    this.darkThemeEnabled = theme === ArmorThemes.dark;
  }

  toggleTheme() {
    this.setTheme(this.darkThemeEnabled ? ArmorThemes.light : ArmorThemes.dark);
  }
}
